import React, { useState } from 'react';

import { Coins, Send } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';

import { Button } from '../ui/Button';

import { DigitBoxInput } from '../ui/DigitBoxInput';

import { YIKI_BET_TYPES, YIKI_BET_TYPE_LABELS, formatYikiPayout } from '../../utils/yikiUtils';
import { confirmPurchase, showError } from '../../utils/swal';
import { formatMoney } from '../../utils/formatMoney';



const YIKI_MIN_BET = 1;

const QUICK_AMOUNTS = [1, 5, 10, 50, 100];



const BET_TABS = [

  { id: YIKI_BET_TYPES.TWO, label: '2 ตัวท้าย', digitLength: 2 },

  { id: YIKI_BET_TYPES.THREE, label: '3 ตัวตรง', digitLength: 3 },

];



export function YikiBetForm({

  room,

  balance,

  onPlaceBet,

  isSubmitting,

  message,

}) {

  const [betType, setBetType] = useState(YIKI_BET_TYPES.TWO);

  const [number, setNumber] = useState('');

  const [amount, setAmount] = useState('1');



  const activeTab = BET_TABS.find((tab) => tab.id === betType);

  const digitLength = activeTab.digitLength;

  const multiplier =

    room &&

    (betType === YIKI_BET_TYPES.TWO

      ? room.multiplierTwo

      : room.multiplierThree);



  const numericAmount = Number(amount) || 0;

  const isComplete = number.length === digitLength;

  const potentialWin = isComplete && multiplier ? numericAmount * multiplier : 0;

  const hasEnoughBalance = balance >= numericAmount;



  const handleTabChange = (nextBetType) => {
    setBetType(nextBetType);
    setNumber('');
  };



  const handleSubmit = async () => {
    if (!isComplete || !room) return;

    if (!hasEnoughBalance) {
      await showError({
        title: 'ยอดไม่พอ',
        text: 'ยอดเงินไม่เพียงพอ กรุณาฝากเงิน',
      });
      return;
    }

    const confirmed = await confirmPurchase({
      title: 'ยืนยันแทงยี่กี',
      html: `<p>เลข <strong>${number}</strong> (${YIKI_BET_TYPE_LABELS[betType]})</p><p class="mt-1">ยอดแทง <strong>${formatMoney(numericAmount)}</strong></p>`,
      confirmText: 'ยืนยันแทง',
    });
    if (!confirmed) return;

    const result = await onPlaceBet({
      roomId: room.id,
      betType,
      number,
      amount: numericAmount,
    });

    if (result) {
      setNumber('');
      setAmount('1');
    }
  };



  return (

    <Card>

      <CardHeader className="bg-gradient-to-r from-brand-red to-brand-red-dark">

        <CardTitle className="flex items-center gap-2 text-white">

          <Coins className="h-5 w-5 text-brand-gold" />

          แทงเลขยี่กี

        </CardTitle>

      </CardHeader>

      <CardContent className="space-y-5">

        <div className="flex rounded-xl border border-red-100 bg-red-50/30 p-1">

          {BET_TABS.map((tab) => (

            <button

              key={tab.id}

              type="button"

              onClick={() => handleTabChange(tab.id)}

              className={`flex flex-1 flex-col items-center rounded-lg px-1 py-2.5 text-sm font-semibold transition-colors ${

                betType === tab.id

                  ? 'bg-brand-red text-white shadow-sm'

                  : 'text-gray-600 hover:bg-white hover:text-brand-red'

              }`}

            >

              {tab.label}
              {room && (
                <span className="mt-1 text-center text-[10px] font-normal leading-tight opacity-90 sm:text-xs">
                  {formatYikiPayout(
                    tab.id === YIKI_BET_TYPES.TWO
                      ? room.multiplierTwo
                      : room.multiplierThree
                  )}
                </span>
              )}

            </button>

          ))}

        </div>



        <DigitBoxInput

          id="yiki-bet"

          length={digitLength}

          value={number}

          onChange={setNumber}

          label={`${YIKI_BET_TYPE_LABELS[betType]} (${digitLength} หลัก)`}

        />



        {betType === YIKI_BET_TYPES.TWO && (

          <p className="text-center text-xs text-gray-500">

            ถูกเมื่อเลข 2 หลักท้ายตรงกับผลรางวัล

          </p>

        )}



        <div>

          <label className="mb-2 block text-sm font-medium text-gray-700">

            ยอดแทง — ขั้นต่ำ ${YIKI_MIN_BET}

          </label>

          <input

            type="number"

            min={YIKI_MIN_BET}

            step="0.01"

            value={amount}

            onChange={(e) => setAmount(e.target.value)}

            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-center text-xl font-semibold focus:border-brand-red focus:outline-none"

          />

          <div className="mt-2 flex flex-wrap gap-2">

            {QUICK_AMOUNTS.map((value) => (

              <button

                key={value}

                type="button"

                onClick={() => setAmount(String(value))}

                className={`rounded-lg border px-3 py-1 text-sm font-medium transition-colors ${

                  Number(amount) === value

                    ? 'border-brand-red bg-red-50 text-brand-red'

                    : 'border-gray-200 text-gray-600 hover:border-brand-gold'

                }`}

              >

                ${value}

              </button>

            ))}

          </div>

        </div>



        {isComplete && room && (

          <div className="rounded-xl bg-gray-50 p-4 text-sm">

            <div className="flex justify-between">

              <span className="text-gray-500">ประเภท</span>

              <span className="font-semibold">{YIKI_BET_TYPE_LABELS[betType]}</span>

            </div>

            <div className="mt-1 flex justify-between gap-2">
              <span className="shrink-0 text-gray-500">อัตราจ่าย</span>
              <span className="text-right text-xs font-semibold leading-tight sm:text-sm">
                {formatYikiPayout(multiplier)}
              </span>
            </div>

            <div className="mt-1 flex justify-between">

              <span className="text-gray-500">รางวัลที่ได้</span>

              <span className="font-bold text-brand-gold">

                ${potentialWin.toLocaleString()}

              </span>

            </div>

          </div>

        )}



        {!hasEnoughBalance && numericAmount >= YIKI_MIN_BET && (

          <p className="text-center text-sm text-brand-red">

            ยอดเงินไม่เพียงพอ กรุณาฝากที่หน้ากระเป๋าเงิน

          </p>

        )}



        {message && (

          <div

            className={`rounded-lg p-3 text-center text-sm ${

              message.includes('สำเร็จ')

                ? 'bg-green-50 text-green-700'

                : 'bg-red-50 text-brand-red'

            }`}

          >

            {message}

          </div>

        )}



        <Button

          size="lg"

          className="w-full"

          disabled={

            isSubmitting ||

            !isComplete ||

            numericAmount < YIKI_MIN_BET ||

            !hasEnoughBalance

          }

          onClick={handleSubmit}

        >

          <Send className="h-4 w-4" />

          {isSubmitting

            ? 'กำลังแทง...'

            : `แทง $${numericAmount || YIKI_MIN_BET}`}

        </Button>

      </CardContent>

    </Card>

  );

}


